import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { businessToday, can, errorText, ORDER_TYPE_LABEL, OrderType, PosFloor, posApi, PosOrderSummary, PosTable, rupees } from './api';
import { usePos } from './PosLayout';

const minutesSince = (iso: string) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

// Floor: every table with its live order (free / occupied / bill printed), plus takeaway and delivery.
// Refreshes every 20 seconds so all tablets stay in step.
export const FloorPage: React.FC = () => {
  const { me, to } = usePos();
  const navigate = useNavigate();
  const [floor, setFloor] = useState<PosFloor | null>(null);
  const [area, setArea] = useState<number | 'all'>('all');
  const [starting, setStarting] = useState<{ type: OrderType; table?: PosTable } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => posApi.floor().then(setFloor).catch((err) => setError(errorText(err))), []);
  useEffect(() => {
    load();
    const t = window.setInterval(load, 20000);
    return () => window.clearInterval(t);
  }, [load]);

  if (!floor) return <p className="pos-muted">{error ?? 'Loading floor...'}</p>;

  const tables = floor.tables.filter((t) => t.isActive && (area === 'all' || t.areaId === area));
  const others = floor.openOrders.filter((o) => o.orderType !== 'DineIn');

  return (
    <>
      {error && <div className="pos-msg err">{error}</div>}
      <div className="pos-floor-head">
        <div className="pos-tabs" style={{ margin: 0 }}>
          <button className={`pos-tab ${area === 'all' ? 'on' : ''}`} onClick={() => setArea('all')}>All tables</button>
          {floor.areas.map((a) => <button key={a.id} className={`pos-tab ${area === a.id ? 'on' : ''}`} onClick={() => setArea(a.id)}>{a.name}</button>)}
        </div>
        <button className="pos-btn gold" onClick={() => setStarting({ type: 'Takeaway' })}>+ Takeaway</button>
        <button className="pos-btn" onClick={() => setStarting({ type: 'Delivery' })}>+ Delivery</button>
        <div className="pos-legend"><span><i style={{ background: '#1f3048' }} />Free</span><span><i style={{ background: '#f59e0b' }} />Occupied</span><span><i style={{ background: '#38bdf8' }} />Bill printed</span></div>
      </div>

      {floor.tables.length === 0 ? (
        <div className="pos-panel">
          <p>No tables yet.</p>
          {can(me.role, 'Manager') ? <Link className="pos-btn gold" to={to('/pos/setup?tab=tables')}>Set up tables and menu</Link> : <p className="pos-muted">Ask a manager to set up tables.</p>}
        </div>
      ) : (
        <div className="pos-tables">
          {tables.map((t) => {
            const o = t.openOrder;
            return (
              <button key={t.id} className={`pos-table ${o ? (o.status === 'Billed' ? 'billed' : 'busy') : ''}`}
                onClick={() => (o ? navigate(to(`/pos/order/${o.id}`)) : setStarting({ type: 'DineIn', table: t }))}>
                <b>{t.name}</b>
                {o ? (
                  <>
                    <span>{rupees(o.total)}</span>
                    <small>{o.covers} guests · {minutesSince(o.createdAt)} min{o.unsentCount ? ` · ${o.unsentCount} not sent` : ''}</small>
                  </>
                ) : <small>{t.seats} seats · free</small>}
              </button>
            );
          })}
        </div>
      )}

      {others.length > 0 && (
        <>
          <h3 style={{ margin: '22px 0 0' }}>Takeaway &amp; delivery</h3>
          <div className="pos-orders">{others.map((o) => <OrderCard key={o.id} order={o} onOpen={() => navigate(to(`/pos/order/${o.id}`))} />)}</div>
        </>
      )}

      {starting && (
        <StartOrderDialog
          type={starting.type}
          table={starting.table}
          onClose={() => setStarting(null)}
          onCreated={(id) => navigate(to(`/pos/order/${id}`))}
        />
      )}
    </>
  );
};

function OrderCard({ order: o, onOpen }: { order: PosOrderSummary; onOpen: () => void }) {
  return (
    <button className="pos-order-card" onClick={onOpen}>
      <b>#{o.orderNo} · {o.tableName ?? ORDER_TYPE_LABEL[o.orderType]}</b>
      <div>{rupees(o.total)} · {o.itemCount} items</div>
      <small className="pos-muted">{o.customerName ?? ''} {o.status === 'Billed' ? '· bill printed' : ''} {o.status === 'Paid' ? '· paid' : ''} {o.status === 'Cancelled' ? '· cancelled' : ''}</small>
    </button>
  );
}

function StartOrderDialog({ type, table, onClose, onCreated }: { type: OrderType; table?: PosTable; onClose: () => void; onCreated: (id: number) => void }) {
  const [covers, setCovers] = useState(type === 'DineIn' ? 2 : 1);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const o = await posApi.createOrder({ orderType: type, tableId: table?.id, covers, customerName: name || undefined, customerPhone: phone || undefined });
      onCreated(o.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <div className="pos-dialog" role="dialog" aria-modal="true" aria-label="New order">
      <div>
        <h3>{type === 'DineIn' ? `Table ${table?.name}` : `New ${ORDER_TYPE_LABEL[type].toLowerCase()} order`}</h3>
        {error && <div className="pos-msg err">{error}</div>}
        {type === 'DineIn' && (
          <label className="pos-field">Guests
            <div className="pos-tabs" style={{ margin: 0 }}>
              {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => <button key={n} type="button" className={`pos-tab ${covers === n ? 'on' : ''}`} onClick={() => setCovers(n)}>{n}</button>)}
            </div>
          </label>
        )}
        <div className="pos-row">
          <label className="pos-field">Customer name (optional)<input className="pos-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} /></label>
          <label className="pos-field">Phone {type === 'Delivery' ? '' : '(optional)'}<input className="pos-input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={16} /></label>
        </div>
        <div className="foot">
          <button className="pos-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pos-btn gold" onClick={start} disabled={busy}>{busy ? 'Starting...' : 'Start order'}</button>
        </div>
      </div>
    </div>
  );
}

// Today's orders (all statuses), newest first.
export const OrdersPage: React.FC = () => {
  const { to } = usePos();
  const navigate = useNavigate();
  const [date, setDate] = useState(businessToday());
  const [orders, setOrders] = useState<PosOrderSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setOrders(null); posApi.orders(date).then(setOrders).catch((err) => setError(errorText(err))); }, [date]);
  return (
    <>
      <div className="pos-floor-head">
        <h2 className="pos-h" style={{ margin: 0 }}>Orders</h2>
        <input type="date" className="pos-input" style={{ width: 180 }} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Business date" />
      </div>
      {error && <div className="pos-msg err">{error}</div>}
      {orders === null ? <p className="pos-muted">Loading...</p> : orders.length === 0 ? <p className="pos-muted">No orders on this day.</p> : (
        <div className="pos-orders">{orders.map((o) => <OrderCard key={o.id} order={o} onOpen={() => navigate(to(`/pos/order/${o.id}`))} />)}</div>
      )}
    </>
  );
};
